import { SelectOption } from '@monorepo/shared/componentsV2/Select';
import { StyledSelect } from '@monorepo/shared/componentsV2/styled/form';
import { isRequired } from '@monorepo/shared/utils/validators';
import { DatasetPropertyResponse, DatasetPropertyType, DatasetResponse } from 'mapistry-shared';
import React, { useMemo } from 'react';
import { useField } from 'react-final-form';

const JOIN_CONDITION_DISALLOWED_PROPERTY_TYPES = [
  // Joining on dates have its special required condition component – DateIntervalCondition
  DatasetPropertyType.DATE,
  DatasetPropertyType.DATE_RANGE,
  DatasetPropertyType.DATETIME,
  // don't let users add a condition on project, while we make all joins project specific under the hood either way
  DatasetPropertyType.PROJECT,
  // joining record properties is illogical
  DatasetPropertyType.RECORD,
];

function filterProperties(
  property: DatasetPropertyResponse,
  selectedPropertyId?: string,
) {
  // Always show the selected property even if we changed rules whether it can be selected or not
  if (property.id === selectedPropertyId) {
    return true;
  }
  return (
    !property.isHidden &&
    !JOIN_CONDITION_DISALLOWED_PROPERTY_TYPES.includes(
      property.type,
    )
  );
}

export function ConditionPicker({
  dataset,
  fieldName,
  label,
  shouldLabelShow,
}: {
  dataset?: DatasetResponse;
  fieldName: string;
  label: string;
  shouldLabelShow: boolean;
}) {
  const {
    input: { value: selectedPropertyId },
  } = useField<string>(fieldName);
  const filteredSchemaProperties = useMemo(
    () =>
      (dataset?.schemaProperties || []).filter(
        (property) => filterProperties(property, selectedPropertyId),
      ),
    [dataset?.schemaProperties, selectedPropertyId],
  );

  const options = useMemo<SelectOption[]>(
    () =>
      filteredSchemaProperties
        .map((property) => ({ label: property.fullName, value: property.id }))
        .sort((optionA, optionB) => optionA.label.localeCompare(optionB.label)),
    [filteredSchemaProperties],
  );

  const isDisabled = !dataset;

  return (
    <StyledSelect
      isLabelHidden={!shouldLabelShow}
      label={label}
      options={options}
      name={fieldName}
      required
      disabled={isDisabled}
      validate={isRequired}
    />
  );
}
